import { Form, Input, InputNumber, Radio } from "antd"
import { BulkWalletEntryMode } from "~/common/constants"
import { formatAmount } from "~/common/utils"
import { bulkEntryModeLabel } from "./labels"
import { BulkWalletAmountField } from "./BulkWalletAmountField"
import {
  BULK_WALLET_LIMITS,
  getBulkWalletAmount,
  getBulkWalletQuantity,
  parseBulkWalletAmount,
} from "./orderForm"

export const BulkWalletFields = ({ className }: { className: string }) => {
  // 范围由表单统一校验，避免 InputNumber 越界输入不触发 onChange 而保留旧值。
  const form = Form.useFormInstance()
  const quantity = Form.useWatch("walletRechargeAmount", form)
  const price = Form.useWatch("purchaseUnitPrice", form)
  const mode =
    Form.useWatch("bulkEntryMode", form) ?? BulkWalletEntryMode.quantity
  const amount = parseBulkWalletAmount(
    Form.useWatch("rechargeAmountYuan", form),
  )
  const byAmount = mode === BulkWalletEntryMode.amount
  const clearInvalidInput = (field: string) => (text: string) => {
    const value = text.trim()
    if (
      value &&
      (!/^-?\d+(\.\d*)?$/.test(value) || !Number.isFinite(Number(value)))
    ) {
      form.setFieldValue(field, null)
    }
  }
  return (
    <>
      <Form.Item
        label="录入方式"
        name="bulkEntryMode"
        initialValue={BulkWalletEntryMode.quantity}
        preserve={false}
        rules={[{ required: true, message: "请选择录入方式" }]}
      >
        <Radio.Group
          options={[
            {
              label: bulkEntryModeLabel[BulkWalletEntryMode.quantity],
              value: BulkWalletEntryMode.quantity,
            },
            {
              label: bulkEntryModeLabel[BulkWalletEntryMode.amount],
              value: BulkWalletEntryMode.amount,
            },
          ]}
          onChange={() =>
            form.resetFields(["walletRechargeAmount", "rechargeAmountYuan"])
          }
        />
      </Form.Item>
      {byAmount ? (
        <BulkWalletAmountField />
      ) : (
        <Form.Item
          label="购买数量"
          name="walletRechargeAmount"
          preserve={false}
          rules={[
            { required: true, message: "请输入购买积点数量" },
            {
              type: "integer",
              ...BULK_WALLET_LIMITS.quantity,
              message: "购买数量必须为 1～100000 的整数",
            },
          ]}
        >
          <InputNumber
            className={className}
            step={1}
            onInput={clearInvalidInput("walletRechargeAmount")}
            changeOnBlur={false}
            placeholder="请输入 1～100000 的整数"
          />
        </Form.Item>
      )}
      <Form.Item
        label="购买单价（分/积点）"
        name="purchaseUnitPrice"
        preserve={false}
        rules={[
          { required: true, message: "请输入购买单价" },
          {
            type: "integer",
            ...BULK_WALLET_LIMITS.purchaseUnitPrice,
            message: "购买单价必须为 22～30 的整数分",
          },
        ]}
      >
        <InputNumber
          className={className}
          step={1}
          onInput={clearInvalidInput("purchaseUnitPrice")}
          onChange={(value) => {
            form.setFieldValue("refundUnitPrice", value)
            // 依赖校验可能已读取旧值；填充后重新校验，替换仍在执行的旧校验。
            void form.validateFields(["refundUnitPrice"]).catch(() => undefined)
          }}
          changeOnBlur={false}
          placeholder="请输入 22～30 的整数，单位：分/积点"
        />
      </Form.Item>
      <Form.Item
        label="退款结算单价（分/积点）"
        name="refundUnitPrice"
        preserve={false}
        dependencies={["purchaseUnitPrice"]}
        extra="退款结算单价不可低于购买单价。"
        rules={[
          { required: true, message: "请输入退款结算单价" },
          {
            type: "integer",
            ...BULK_WALLET_LIMITS.refundUnitPrice,
            message: "退款结算单价必须为 20～30 的整数分",
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              const purchaseUnitPrice = getFieldValue("purchaseUnitPrice")
              if (
                typeof value === "number" &&
                typeof purchaseUnitPrice === "number" &&
                value < purchaseUnitPrice
              ) {
                return Promise.reject(new Error("退款结算单价不可低于购买单价"))
              }
              return Promise.resolve()
            },
          }),
        ]}
      >
        <InputNumber
          className={className}
          step={1}
          onInput={clearInvalidInput("refundUnitPrice")}
          changeOnBlur={false}
          placeholder="请输入 20～30 的整数，单位：分/积点"
        />
      </Form.Item>
      {byAmount ? (
        <Form.Item
          label="到账积点数量"
          extra="按充值金额 ÷ 购买单价向上取整，最终数量由后端计算。"
        >
          <Input
            id="bulkWalletQuantityPreview"
            readOnly
            value={getBulkWalletQuantity(amount, price) ?? ""}
            suffix="积点"
          />
        </Form.Item>
      ) : (
        <Form.Item
          label="订单金额（元）"
          extra="按购买数量 × 购买单价预览，最终金额由后端计算。"
        >
          <Input
            readOnly
            value={formatAmount(getBulkWalletAmount(quantity, price))}
          />
        </Form.Item>
      )}
    </>
  )
}
